import * as React from "react";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles, FormControlLabel } from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import { Payment } from '../../api/payments';
import { Dispatch } from 'redux';
import { closeAddPaymentDialog } from '../../actions/store/app-actions';
import { refreshInquiry, updateInquiry } from '../../actions/inquiry-actions';
import { addHours } from 'date-fns';
import { formatDistanceStrictToNow } from '../../util/date-utility';
import { conf } from '../../conf';
import { addPaymentAndUpdateStore, updatePaymentAndUpdateStore } from '../../actions/payment-actions';

interface State {
	formDisabled: boolean;
	dialogAmount: string;
	dialogItemName: string;
	dialogName: string;
	dialogEmail: string;
	dialogValidHours: number;
	dialogMessage?: string;
	dialogConvertToReservation: boolean;
}

export interface AddPaymentDialogProps {}

type Props = AddPaymentDialogProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	WithStyles<typeof styles> &
	InjectedTranslateProps;

export class AddPaymentDialog extends React.Component<Props, {}> {
	public readonly state: State = {
		formDisabled: false,
		dialogAmount: this.props.passedAmount ? this.props.passedAmount : conf.payments.defaultAmount.toFixed(2),
		dialogItemName: "Anzahlung Tischreservierung",
		dialogName: this.props.passedName ? this.props.passedName : "",
		dialogEmail: this.props.passedEmail ? this.props.passedEmail : "",
		dialogValidHours: this.props.passedValidHours ? this.props.passedValidHours : conf.payments.defaultValidHours,
		dialogMessage: this.props.passedMessage ? this.props.passedMessage : "",
		dialogConvertToReservation: true
	};

	private form: HTMLFormElement;

	private handleFormRef = (ref: HTMLFormElement) => {
		this.form = ref;
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (this.props.open === false && nextProps.open === true) {
			this.setStateFromProps(nextProps);
		}
	}

	private setStateFromProps = (props: Props) => {
		this.setState({
			formDisabled: false,
			dialogAmount: props.passedAmount ? props.passedAmount : conf.payments.defaultAmount.toFixed(2),
			dialogName: props.passedName ? props.passedName : "",
			dialogEmail: props.passedEmail ? props.passedEmail : "",
			dialogValidHours: props.passedValidHours ? props.passedValidHours : conf.payments.defaultValidHours,
			dialogMessage: props.passedMessage ? props.passedMessage : "",
			dialogConvertToReservation: true
		});
	};

	private handleDialogAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const dialogAmount = parseFloat(event.currentTarget.value);

		this.setState({
			dialogAmount: isNaN(dialogAmount) ? "" : dialogAmount
		});
	};

	private handleDialogAmountBlur = () => {
		const { dialogAmount } = this.state;

		const roundedAmount =
			dialogAmount === ""
				? "0"
				: parseFloat(dialogAmount).toFixed(2);

		this.setState({ dialogAmount: roundedAmount });
	};

	private handleDialogEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogEmail: event.currentTarget.value });
	};

	private handleDialogItemNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogItemName: event.currentTarget.value });
	};

	private handleDialogValidHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogValidHours: event.currentTarget.value });
	};

	private handleDialogNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogName: event.currentTarget.value });
	};

	private handleDialogMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogMessage: event.currentTarget.value });
	};

	private handleDialogClose = () => {
		this.props.close();
	};

	private handleDialogSubmitClick = async () => {
		const { formDisabled, dialogAmount, dialogName, dialogEmail, dialogItemName, dialogValidHours, dialogMessage, dialogConvertToReservation } = this.state;
		const { inquiryId, paymentId } = this.props;

		if (formDisabled) return;
		if (!this.form.reportValidity()) return;
		if (dialogItemName.trim().length == 0) return;

		this.setState({ formDisabled: true })

		const amount = parseFloat(dialogAmount);
		const isInquiry = inquiryId && inquiryId !== "";

		const payload: Payment = {
			amount,
			currency: 'EUR',
			name: dialogName,
			items: [{
				amount,
				name: dialogItemName
			}],
			transactionName: dialogItemName,
			expirationDate: addHours(new Date(), dialogValidHours).toISOString(),
			email: dialogEmail,
		};

		if (isInquiry) {
			payload.inquiryId = inquiryId;
		}

		if (Boolean(dialogMessage)) {
			payload.message = dialogMessage;
		}

		if (paymentId && paymentId !== "") {
			await updatePaymentAndUpdateStore(paymentId, payload);
		} else {
			await addPaymentAndUpdateStore(payload);
		}

		if (isInquiry) {
			if (dialogConvertToReservation == true && !Boolean(paymentId)) {
				await updateInquiry(inquiryId, {
					moved: true
				});
			} else {
				await refreshInquiry(inquiryId);
			}
		}

		this.handleDialogClose();
	}

	private generateDurationHelperText = () => {
		const { dialogValidHours } = this.state;

		if (dialogValidHours < 24) return;

		let prefix = "";

		if (dialogValidHours % 24 !== 0) {
			prefix = "~ " + prefix;
		}

		return prefix + formatDistanceStrictToNow(addHours(new Date(), dialogValidHours));
	};

	public render() {
		const {
			classes,
			open,
			inquiryId,
			paymentId
		} = this.props;
		const {
			formDisabled,
			dialogAmount,
			dialogName,
			dialogEmail,
			dialogItemName,
			dialogValidHours,
			dialogMessage,
			dialogConvertToReservation
		} = this.state;

		return (
			<Dialog
				open={open}
				onClose={this.handleDialogClose}
			>
				<DialogTitle>Neue Zahlung anfordern</DialogTitle>
				<DialogContent>
					<form ref={this.handleFormRef}>
						<div className={classes.formRow}>
							<TextField
								autoFocus
								margin="dense"
								type="number"
								required
								label="Betrag"
								className={classes.textField}
								value={isNaN(parseFloat(dialogAmount)) ? "" : dialogAmount}
								disabled={formDisabled}
								onChange={this.handleDialogAmountChange}
								onBlur={this.handleDialogAmountBlur}
								InputProps={{
									startAdornment: <InputAdornment position="start">€</InputAdornment>
								}}
								inputProps={{
									min: "1",
									step: "0.01"
								}}
							/>
							<TextField
								margin="dense"
								type="number"
								value={dialogValidHours}
								required
								label="Gültigkeit"
								helperText={this.generateDurationHelperText()}
								className={classes.textField}
								disabled={formDisabled}
								onChange={this.handleDialogValidHoursChange}
								InputProps={{
									endAdornment: <InputAdornment position="end">Stunden</InputAdornment>
								}}
								inputProps={{
									min: 12,
									max: 336
								}}
							/>
						</div>
						<TextField
							margin="dense"
							type="text"
							value={dialogItemName}
							fullWidth
							required
							label="Zahlungsbeschreibung"
							disabled={formDisabled}
							onChange={this.handleDialogItemNameChange}
						/>
						<div className={classes.formRow}>
							<TextField
								margin="dense"
								type="text"
								value={dialogName}
								required
								label="Name"
								className={classes.textField}
								disabled={formDisabled}
								onChange={this.handleDialogNameChange}
							/>
							<TextField
								margin="dense"
								type="email"
								value={dialogEmail}
								required
								label="E-Mail Adresse"
								className={classes.textField}
								disabled={formDisabled}
								onChange={this.handleDialogEmailChange}
							/>
						</div>
						<TextField
							margin="dense"
							type="text"
							value={dialogMessage}
							fullWidth
							required
							label="Nachricht"
							multiline={true}
							disabled={Boolean(inquiryId) || formDisabled}
							rows={3}
							onChange={this.handleDialogMessageChange}
							/>
						{Boolean(inquiryId) && !Boolean(paymentId) && (
							<FormControlLabel
								className={classes.checkbox}
								control={
									<Checkbox
										id="checkedIn"
										color="primary"
										checked={dialogConvertToReservation}
										disabled={formDisabled}
										onChange={event =>
											this.setState({
												dialogConvertToReservation: event.target.checked
											})
										}
									/>
								}
								label="Anfrage entfernen und zu den Reservierungen verschieben"
							/>
						)}
					</form>
				</DialogContent>
				<DialogActions>
					<Button
						className="close-add-payment-dialog-button"
						disabled={formDisabled}
						onClick={this.handleDialogClose}
					>
						Abbrechen
					</Button>
					<Button
						className="submit-payment-button"
						disabled={formDisabled}
						onClick={this.handleDialogSubmitClick}
						color="primary"
					>
						Senden
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	open: state.addPaymentDialogOpen,
	inquiryId: state.addPaymentDialogInquiryId,
	paymentId: state.addPaymentDialogPaymentId,
	passedEmail: state.addPaymentDialogEmail,
	passedName: state.addPaymentDialogName,
	passedAmount: state.addPaymentDialogAmount,
	passedValidHours: state.addPaymentDialogValidHours,
	passedMessage: state.addPaymentDialogMessage
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeAddPaymentDialog())
});

const styles = (theme: Theme) =>
	createStyles({
		formRow: {
			display: "flex",
			margin: -10,

			"@media (max-width: 600px)": {
				display: "block"
			}
		},
		textField: {
			margin: 10,
			width: "calc(50% - 20px)",

			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
		checkbox: {
			marginTop: 8,
			userSelect: "none",
			containerType: "inline-size",
			width: "100%",
			"& span": {  
				lineHeight: 1.2,
				paddingLeft: 5
			}
		},
	}); 

export const ConnectedAddPaymentDialog = translate("translations")(
	withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(AddPaymentDialog))
);
